import { Link } from 'react-router-dom';

interface ArticleCardProps {
  title: string;
  slug: string;
  category: string;
  categoryName: string;
  excerpt: string;
  author: string;
  authorImage: string;
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
}

export default function ArticleCard({
  title,
  slug,
  category,
  categoryName,
  excerpt,
  author,
  authorImage,
  date,
  readTime,
  image,
  featured = false,
}: ArticleCardProps) {
  if (featured) {
    return (
      <Link to={`/article/${slug}`} className="group block cursor-pointer">
        <article className="bg-background-50 rounded-xl overflow-hidden border border-background-200/60 transition-all duration-300 hover:border-primary-200/50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="relative h-56 lg:h-full overflow-hidden">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-3">
                <Link
                  to={`/category/${category}`}
                  onClick={(e) => e.stopPropagation()}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{readTime}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                {excerpt}
              </p>
              <div className="flex items-center gap-3">
                <img
                  src={authorImage}
                  alt={author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="text-sm text-foreground-600 font-medium">{author}</span>
              </div>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={`/article/${slug}`} className="group block cursor-pointer">
      <article className="bg-background-50 rounded-xl overflow-hidden border border-background-200/60 transition-all duration-300 hover:border-primary-200/50 h-full flex flex-col">
        <div className="relative h-48 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 rounded-full bg-background-50/90 text-primary-700 text-xs font-medium whitespace-nowrap">
              {categoryName}
            </span>
          </div>
        </div>
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-foreground-400">{date}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-400">{readTime}</span>
          </div>
          <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {title}
          </h3>
          <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2 flex-1">
            {excerpt}
          </p>
          <div className="flex items-center gap-2 mt-auto pt-3 border-t border-background-100">
            <img
              src={authorImage}
              alt={author}
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-xs text-foreground-500">{author}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}